import { Controller } from "@hotwired/stimulus"

// Lazily builds the TradingView Advanced Chart embed only when it nears the
// viewport, keeping the heavy third-party iframe off the initial load path
// (mobile Core Web Vitals). It also injects the widget with the *resolved*
// page theme read from <html data-bs-theme>, which fixes the anonymous
// OS-dark case: the server can't read prefers-color-scheme, so it renders the
// chart config as "light", leaving a light chart on a dark page.
//
// The loader (embed-widget-advanced-chart.js) relies on document.currentScript
// and walks up to the .tradingview-widget-container to find the __widget div,
// so the script must be appended to this.element (the container), not the
// __widget. currentScript IS set for a dynamically-created external src script
// during its execution, so this works.
export default class extends Controller {
  static values = { config: Object }

  // Server-rendered heights (450-500px) are sized for desktop; on a phone
  // that's ~⅔ of the screen for the chart alone. Clamp on phone viewports —
  // and shrink the reserved container in connect(), before the lazy load,
  // so the layout settles once rather than shifting when the iframe arrives.
  static MOBILE_MAX_HEIGHT = 300

  connect() {
    this.loaded = false

    if (window.innerWidth < 576 && this.configValue.height > this.constructor.MOBILE_MAX_HEIGHT) {
      this.mobileHeight = this.constructor.MOBILE_MAX_HEIGHT
      this.element.style.height = `${this.mobileHeight + 32}px`
    }

    if (!("IntersectionObserver" in window)) {
      this.load()
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.load()
      },
      { rootMargin: "300px" }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  load() {
    if (this.loaded) return
    this.loaded = true
    this.observer?.disconnect()

    const config = { ...this.configValue, theme: this.resolvedTheme() }
    if (this.mobileHeight) config.height = this.mobileHeight

    const script = document.createElement("script")
    script.type = "text/javascript"
    script.async = true
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js"
    script.textContent = JSON.stringify(config)

    this.element.appendChild(script)
  }

  resolvedTheme() {
    return document.documentElement.getAttribute("data-bs-theme") === "dark" ? "dark" : "light"
  }
}
